import { useMutation } from "@apollo/client/react";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { toast } from "sonner";
import {
  GetRecurringTransactionsDocument,
  CreateRecurringTransactionDocument,
  UpdateRecurringTransactionDocument,
  DeleteRecurringTransactionDocument,
  type CreateRecurringTransactionMutation,
  type UpdateRecurringTransactionMutation,
  type DeleteRecurringTransactionMutation,
} from "@/src/gql/graphql";
import type { RecurringTransaction } from "@/lib/types/recurring-transactions";
import { QB_REALM } from "@/lib/integrations/quickbooks/realms";

export function useCreateRecurringTransaction() {
  const queryClient = useQueryClient();

  const [mutate, { loading, error }] = useMutation<CreateRecurringTransactionMutation>(
    CreateRecurringTransactionDocument
  );

  const createTransaction = useCallback(
    async (input: {
      customer_id: string;
      item: string;
      description?: string;
      amount: number;
      quantity?: number;
      quickbooks_realm_id?: string;
    }) => {
      try {
        const { amount, quantity, quickbooks_realm_id, ...rest } = input;
        const quantityValue =
          quantity !== undefined && quantity !== null ? String(quantity) : "1";

        const result = await mutate({
          variables: {
            input: {
              ...rest,
              amount: String(amount),
              quantity: quantityValue,
              // Default new lines to the West Tennessee Consulting realm (all
              // existing rows are WTC); the create form can override to Schooley
              // Mitchell.
              quickbooks_realm_id:
                quickbooks_realm_id || QB_REALM.WEST_TENNESSEE_CONSULTING,
            },
          },
          refetchQueries: [{ query: GetRecurringTransactionsDocument }],
        });

        const record = result.data?.insertIntorecurring_transactionsCollection?.records?.[0];
        if (record) {
          void queryClient.invalidateQueries({ queryKey: ["recurring-transactions"] });
          toast.success("Recurring transaction created successfully");
        }
        return record;
      } catch (err) {
        toast.error("Failed to create recurring transaction");
        throw err;
      }
    },
    [mutate, queryClient]
  );

  return { createTransaction, loading, error };
}

export function useUpdateRecurringTransaction() {
  const queryClient = useQueryClient();

  const [mutate, { loading, error }] = useMutation<UpdateRecurringTransactionMutation>(
    UpdateRecurringTransactionDocument
  );

  const updateTransaction = useCallback(
    async (id: string, input: Partial<RecurringTransaction>) => {
      try {
        const result = await mutate({
          variables: {
            id,
            input: {
              ...(input.customer_id !== undefined && { customer_id: input.customer_id }),
              ...(input.item !== undefined && { item: input.item }),
              ...(input.description !== undefined && { description: input.description }),
              ...(input.amount !== undefined && { amount: String(input.amount) }),
              ...(input.quantity !== undefined && { quantity: String(input.quantity) }),
              ...(input.sync_status !== undefined && { sync_status: input.sync_status }),
              ...(input.last_synced_at !== undefined && { last_synced_at: input.last_synced_at }),
              ...(input.last_synced_month !== undefined && {
                last_synced_month: input.last_synced_month,
              }),
              ...(input.quickbooks_id !== undefined && { quickbooks_id: input.quickbooks_id }),
              ...(input.quickbooks_realm_id !== undefined && {
                quickbooks_realm_id: input.quickbooks_realm_id,
              }),
            },
          },
          refetchQueries: [{ query: GetRecurringTransactionsDocument }],
        });

        const record = result.data?.updaterecurring_transactionsCollection?.records?.[0];
        if (record) {
          void queryClient.invalidateQueries({ queryKey: ["recurring-transactions"] });
          toast.success("Recurring transaction updated successfully");
        }
        return record;
      } catch (err) {
        toast.error("Failed to update recurring transaction");
        throw err;
      }
    },
    [mutate, queryClient]
  );

  return { updateTransaction, loading, error };
}

export function useDeleteRecurringTransaction() {
  const queryClient = useQueryClient();

  const [mutate, { loading, error }] = useMutation<DeleteRecurringTransactionMutation>(
    DeleteRecurringTransactionDocument
  );

  const deleteTransaction = useCallback(
    async (id: string) => {
      try {
        const result = await mutate({
          variables: { id },
          refetchQueries: [{ query: GetRecurringTransactionsDocument }],
        });

        const record = result.data?.deleteFromrecurring_transactionsCollection?.records?.[0];
        if (record) {
          void queryClient.invalidateQueries({ queryKey: ["recurring-transactions"] });
          toast.success("Recurring transaction deleted successfully");
        }
        return record;
      } catch (err) {
        toast.error("Failed to delete recurring transaction");
        throw err;
      }
    },
    [mutate, queryClient]
  );

  return { deleteTransaction, loading, error };
}
